import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Textarea } from '../ui/textarea';
import { 
  Mic, 
  MicOff, 
  Square, 
  Play, 
  Pause, 
  Trash2, 
  Send, 
  Loader2,
  CheckCircle,
  AlertCircle,
  Volume2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * VoiceRecorder Component
 * Allows agents to record audio, transcribe it, edit the transcription,
 * and then analyze it for diagnosis (skincare or health)
 * 
 * Props:
 * - mode: 'skincare' | 'health' - determines analysis type
 * - onAnalysisComplete: (result) => void - callback with analysis results
 * - clientName: optional client name for context
 * - clientPhone: optional client phone
 * - language: 'fr' | 'en' - UI language
 */
const VoiceRecorder = ({ 
  mode = 'skincare', 
  onAnalysisComplete, 
  clientName = '',
  clientPhone = '',
  language = 'fr' 
}) => {
  // Recording states
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  
  // Transcription states
  const [transcription, setTranscription] = useState('');
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcriptionComplete, setTranscriptionComplete] = useState(false);
  
  // Analysis states
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  
  // Refs
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [audioUrl]);

  // Start recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Use webm format which is widely supported
      const options = { mimeType: 'audio/webm;codecs=opus' };
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        options.mimeType = 'audio/webm';
      }
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        options.mimeType = 'audio/ogg;codecs=opus';
      }
      
      mediaRecorderRef.current = new MediaRecorder(stream, options);
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: options.mimeType });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current.start(100); // Collect data every 100ms
      setIsRecording(true);
      setIsPaused(false);
      setRecordingTime(0);
      setTranscription('');
      setTranscriptionComplete(false);
      setAnalysisResult(null);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (error) {
      console.error('Recording error:', error);
      toast.error(language === 'fr' 
        ? 'Impossible d\'accéder au microphone' 
        : 'Cannot access microphone'
      );
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  // Pause/Resume recording
  const togglePause = () => {
    if (mediaRecorderRef.current && isRecording) {
      if (isPaused) {
        mediaRecorderRef.current.resume();
        timerRef.current = setInterval(() => {
          setRecordingTime(prev => prev + 1);
        }, 1000);
      } else {
        mediaRecorderRef.current.pause();
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
      }
      setIsPaused(!isPaused);
    }
  };

  // Delete recording
  const deleteRecording = () => {
    setAudioBlob(null);
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }
    setTranscription('');
    setTranscriptionComplete(false);
    setAnalysisResult(null);
    setRecordingTime(0);
  };

  // Transcribe audio
  const transcribeAudio = async () => {
    if (!audioBlob) return;
    
    setIsTranscribing(true);
    
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      formData.append('language', 'fr');
      
      const response = await axios.post(`${API_URL}/voice/transcribe`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        setTranscription(response.data.transcription);
        setTranscriptionComplete(true);
        toast.success(language === 'fr' ? 'Transcription terminée' : 'Transcription complete');
      }
    } catch (error) {
      console.error('Transcription error:', error);
      toast.error(error.response?.data?.detail || (language === 'fr' 
        ? 'Erreur de transcription' 
        : 'Transcription error'
      ));
    } finally {
      setIsTranscribing(false);
    }
  };

  // Analyze transcription
  const analyzeTranscription = async () => {
    if (!transcription.trim()) {
      toast.error(language === 'fr' ? 'Veuillez saisir une description' : 'Please enter a description');
      return;
    }
    
    setIsAnalyzing(true);
    
    try {
      const endpoint = mode === 'skincare' 
        ? `${API_URL}/voice/analyze/skincare`
        : `${API_URL}/voice/analyze/health`;
      
      const payload = mode === 'skincare'
        ? { transcription, client_name: clientName, client_phone: clientPhone }
        : { transcription, patient_name: clientName };
      
      const response = await axios.post(endpoint, payload);
      
      if (response.data.success) {
        setAnalysisResult(response.data);
        toast.success(language === 'fr' ? 'Analyse terminée' : 'Analysis complete');
        
        if (onAnalysisComplete) {
          onAnalysisComplete(response.data);
        }
      }
    } catch (error) {
      console.error('Analysis error:', error);
      toast.error(error.response?.data?.detail || (language === 'fr' 
        ? 'Erreur d\'analyse' 
        : 'Analysis error'
      ));
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Card className="border-2 border-dashed border-gray-300 bg-gradient-to-br from-gray-50 to-white">
      <CardContent className="p-6 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`p-2 rounded-full ${isRecording ? 'bg-red-100' : 'bg-blue-100'}`}>
              {isRecording ? (
                <Mic className="w-5 h-5 text-red-600 animate-pulse" />
              ) : (
                <Mic className="w-5 h-5 text-blue-600" />
              )}
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">
                {language === 'fr' ? 'Diagnostic Vocal' : 'Voice Diagnosis'}
              </h3>
              <p className="text-xs text-gray-500">
                {mode === 'skincare' 
                  ? (language === 'fr' ? 'Décrivez les symptômes cutanés' : 'Describe skin symptoms')
                  : (language === 'fr' ? 'Dictez les mesures de santé' : 'Dictate health measurements')
                }
              </p>
            </div>
          </div>
          
          {/* Recording timer */}
          {(isRecording || recordingTime > 0) && (
            <div className={`px-3 py-1 rounded-full text-sm font-mono ${isRecording ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}`}>
              {formatTime(recordingTime)}
            </div>
          )}
        </div>

        {/* Recording controls */}
        <div className="flex items-center justify-center gap-3 py-4">
          {!isRecording && !audioBlob && (
            <Button
              onClick={startRecording}
              size="lg"
              className="bg-red-500 hover:bg-red-600 text-white rounded-full h-16 w-16"
              data-testid="start-recording-btn"
            >
              <Mic className="w-6 h-6" />
            </Button>
          )}
          
          {isRecording && (
            <>
              <Button
                onClick={togglePause}
                size="lg"
                variant="outline"
                className="rounded-full h-12 w-12"
              >
                {isPaused ? <Play className="w-5 h-5" /> : <Pause className="w-5 h-5" />}
              </Button>
              
              <Button
                onClick={stopRecording}
                size="lg"
                className="bg-gray-900 hover:bg-gray-800 text-white rounded-full h-16 w-16"
                data-testid="stop-recording-btn"
              >
                <Square className="w-6 h-6" />
              </Button>
            </>
          )}
          
          {audioBlob && !isRecording && (
            <>
              {/* Playback */}
              <Button
                onClick={() => audioRef.current?.play()}
                size="lg"
                variant="outline"
                className="rounded-full h-12 w-12"
              >
                <Volume2 className="w-5 h-5" />
              </Button>
              
              {/* Transcribe */}
              <Button
                onClick={transcribeAudio}
                disabled={isTranscribing || transcriptionComplete}
                size="lg"
                className={`rounded-full h-14 px-6 ${
                  transcriptionComplete 
                    ? 'bg-green-500 hover:bg-green-600' 
                    : 'bg-blue-500 hover:bg-blue-600'
                } text-white`}
                data-testid="transcribe-btn"
              >
                {isTranscribing ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : transcriptionComplete ? (
                  <>
                    <CheckCircle className="w-5 h-5 mr-2" />
                    {language === 'fr' ? 'Transcrit' : 'Transcribed'}
                  </>
                ) : (
                  language === 'fr' ? 'Transcrire' : 'Transcribe'
                )}
              </Button>
              
              {/* Delete */}
              <Button
                onClick={deleteRecording}
                size="lg"
                variant="outline"
                className="rounded-full h-12 w-12 text-red-500 hover:text-red-700 hover:border-red-300"
              >
                <Trash2 className="w-5 h-5" />
              </Button>
            </>
          )}
        </div>
        
        {/* Hidden audio element for playback */}
        {audioUrl && (
          <audio ref={audioRef} src={audioUrl} className="hidden" />
        )}

        {/* Transcription area */}
        {(transcription || transcriptionComplete || audioBlob) && (
          <div className="space-y-3">
            <Label className="text-sm font-medium text-gray-700">
              {language === 'fr' ? 'Transcription (modifiable)' : 'Transcription (editable)'}
            </Label>
            <Textarea
              value={transcription}
              onChange={(e) => setTranscription(e.target.value)}
              placeholder={language === 'fr' 
                ? 'La transcription apparaîtra ici. Vous pouvez la modifier avant l\'analyse...'
                : 'Transcription will appear here. You can edit it before analysis...'
              }
              className="min-h-[120px] resize-none"
              data-testid="transcription-textarea"
            />
            
            {/* Analyze button */}
            {transcription.trim() && (
              <Button
                onClick={analyzeTranscription}
                disabled={isAnalyzing}
                className="w-full bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white"
                data-testid="analyze-btn"
              >
                {isAnalyzing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {language === 'fr' ? 'Analyse en cours...' : 'Analyzing...'}
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    {language === 'fr' ? 'Analyser et générer le diagnostic' : 'Analyze and generate diagnosis'}
                  </>
                )}
              </Button>
            )}
          </div>
        )}

        {/* Analysis results preview */}
        {analysisResult && (
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="font-semibold text-green-800">
                {language === 'fr' ? 'Analyse terminée' : 'Analysis complete'}
              </span>
            </div>
            
            {mode === 'skincare' && (
              <div className="text-sm text-green-700">
                <p><strong>{language === 'fr' ? 'Type de peau' : 'Skin type'}:</strong> {analysisResult.skin_type}</p>
                <p><strong>{language === 'fr' ? 'Problèmes' : 'Concerns'}:</strong> {analysisResult.main_concerns?.join(', ')}</p>
                <p><strong>{language === 'fr' ? 'Sévérité' : 'Severity'}:</strong> {analysisResult.severity}</p>
              </div>
            )}
            
            {mode === 'health' && analysisResult.extracted_data && (
              <div className="text-sm text-green-700 space-y-1">
                {analysisResult.extracted_data.systolic && (
                  <p><strong>TA:</strong> {analysisResult.extracted_data.systolic}/{analysisResult.extracted_data.diastolic} mmHg</p>
                )}
                {analysisResult.extracted_data.blood_sugar && (
                  <p><strong>{language === 'fr' ? 'Glycémie' : 'Blood sugar'}:</strong> {analysisResult.extracted_data.blood_sugar} g/L</p>
                )}
                {analysisResult.extracted_data.weight_kg && (
                  <p><strong>{language === 'fr' ? 'Poids' : 'Weight'}:</strong> {analysisResult.extracted_data.weight_kg} kg</p>
                )}
                {analysisResult.extracted_data.height_cm && (
                  <p><strong>{language === 'fr' ? 'Taille' : 'Height'}:</strong> {analysisResult.extracted_data.height_cm} cm</p>
                )}
                {analysisResult.interpretation && (
                  <p className="mt-2 italic">{analysisResult.interpretation}</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Instructions */}
        {!isRecording && !audioBlob && (
          <div className="text-center text-sm text-gray-500 py-2">
            <p>{language === 'fr' 
              ? 'Appuyez sur le bouton pour commencer l\'enregistrement' 
              : 'Press the button to start recording'
            }</p>
            {mode === 'skincare' && (
              <p className="text-xs mt-1 text-gray-400">
                {language === 'fr' 
                  ? 'Ex: "Le client a une peau grasse avec des boutons sur le front et des taches brunes sur les joues..."'
                  : 'Ex: "The client has oily skin with pimples on the forehead and brown spots on the cheeks..."'
                }
              </p>
            )}
            {mode === 'health' && (
              <p className="text-xs mt-1 text-gray-400">
                {language === 'fr' 
                  ? 'Ex: "Tension artérielle 14/9, glycémie 1.2 grammes par litre, poids 75 kilos, taille 1 mètre 70..."'
                  : 'Ex: "Blood pressure 140/90, blood sugar 1.2 grams per liter, weight 75 kg, height 170 cm..."'
                }
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Label component for internal use
const Label = ({ children, className = '' }) => (
  <label className={`block ${className}`}>{children}</label>
);

export default VoiceRecorder;
